/**
 * 画面詳細の差分の導出。**案件を選んでいるときだけ意味を持つ。**
 *
 * 変更前（`side: 'before'`）と変更後（`side: 'after'`）の 2 回の読取を、コンポーネントの番号で突き合わせる。
 * 画面詳細は案件モードで 欄 ／ 変更前 ／ 変更後 の 3 列に並べる（案件モードの原則: 中身を見る
 * 画面は左に as-is、右に to-be）。そのため 1 行ごとに**変更前のコンポーネントと変更後のコンポーネントの対**を返す。
 * 案件が落としたコンポーネントは削除の行として残す。落とすと、コンポーネント表から消えたことが画面に出ない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面詳細のコンポーネント表について、案件の変更前と変更後のコンポーネントを突き合わせ、追加・更新・削除と変わった欄を導出する。
 * @graph-connects none
 */
import type { ScreenComponentRow, ScreenRef } from '@cortex/domain-spec-console';

import type { SpecDiffState } from '../components/spec/SpecDiffState.js';

/**
 * コンポーネント表の 1 行。差分の状態と、変わった欄の旗を持つ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面詳細のコンポーネント表 1 行（コンポーネントの中身・差分の状態・変わった欄）
 * @graph-connects none
 */
export interface ScreenComponentDiffRow {
  /** 描くコンポーネント。削除の行は変更前の姿を持つ */
  readonly component: ScreenComponentRow;
  /** 変更前のコンポーネント。変更前に無い（追加の行・案件を選んでいない）なら null */
  readonly previous: ScreenComponentRow | null;
  /** 追加 / 更新 / 削除 / 変化なし */
  readonly state: SpecDiffState;
  /** 欄ごとに変わったか */
  readonly changed: ScreenComponentChanged;
}

/**
 * コンポーネント 1 件の欄ごとの変化。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business コンポーネント 1 件のどの欄が案件で変わったか（名前・種別・変数部の表示ロジックの用語・操作する先・表示条件・遷移先）
 * @graph-connects none
 */
export interface ScreenComponentChanged {
  /** 表示名が変わったか */
  readonly label: boolean;
  /** 種別が変わったか */
  readonly kind: boolean;
  /** 変数部の表示ロジックの用語（番号 ・ 名前 ・ 定義）が変わったか */
  readonly subject: boolean;
  /** 操作する先が変わったか */
  readonly operates: boolean;
  /** 表示条件が変わったか */
  readonly visibleWhen: boolean;
  /** 遷移先が変わったか */
  readonly transitionTo: boolean;
}

/** 何も変わっていないコンポーネントの旗。 @graph-connects none */
const NOTHING_CHANGED: ScreenComponentChanged = {
  label: false,
  kind: false,
  subject: false,
  operates: false,
  visibleWhen: false,
  transitionTo: false,
};

/** 参照 1 件が同じか。番号と表示名の両方で見る。 @graph-connects none */
function sameRef(left: ScreenRef | null, right: ScreenRef | null): boolean {
  if (left === null || right === null) return left === right;
  return left.id === right.id && left.label === right.label;
}

/**
 * 参照の並びを 1 本の鍵にする。並び順も含めて見る（読取が id の昇順で返すため）。
 *
 * 番号と表示名を繋いで比べるのは、**要素ごとの突き合わせに「並びの長さが違う」経路が要らなく
 * なる**ためである。長さで先に分ける形にすると、長さが同じときにしか通らない添字の保険が
 * 残って、どのデータでも到達しない枝になる。
 * @graph-connects none
 */
function refsKey(refs: readonly ScreenRef[]): string {
  return refs.map((ref) => `${String(ref.id)}:${ref.label}`).join('\n');
}

/**
 * 変数部の表示ロジックの用語が同じか。用語の番号・名前・定義の 3 つで見る。欄の無い形（API より先に画面が
 * 出た間）は `null` と同じに扱う。
 * @graph-connects sameRef [calls] 番号と名前の突き合わせ
 */
function sameSubject(after: ScreenComponentRow, before: ScreenComponentRow): boolean {
  const left = after.subject ?? null;
  const right = before.subject ?? null;
  return sameRef(left, right) && left?.description === right?.description;
}

/**
 * コンポーネント 1 件の欄ごとの変化を出す。
 * @graph-connects sameRef [calls] 参照 1 件の突き合わせ
 * @graph-connects sameSubject [calls] 変数部の表示ロジックの用語の突き合わせ
 * @graph-connects refsKey [calls] 遷移先の並びの突き合わせ
 */
function changedOf(after: ScreenComponentRow, before: ScreenComponentRow): ScreenComponentChanged {
  return {
    label: after.label !== before.label,
    kind: after.kind !== before.kind,
    subject: !sameSubject(after, before),
    operates:
      !sameRef(after.operates, before.operates) ||
      (after.operates?.nodeType ?? null) !== (before.operates?.nodeType ?? null),
    visibleWhen: !sameRef(after.visibleWhen, before.visibleWhen),
    transitionTo: refsKey(after.transitionTo) !== refsKey(before.transitionTo),
  };
}

/** 欄が 1 つでも変わっていれば真。 @graph-connects none */
function anyChanged(changed: ScreenComponentChanged): boolean {
  return (
    changed.label ||
    changed.kind ||
    changed.subject ||
    changed.operates ||
    changed.visibleWhen ||
    changed.transitionTo
  );
}

/**
 * コンポーネント表の行を組む。案件を選んでいなければ全行を「変化なし」で返す。
 *
 * 削除されたコンポーネントは**末尾ではなく番号の昇順**に混ぜる。末尾へ寄せると、案件が触ったコンポーネントが
 * 表の下に固まり、同じ画面のコンポーネントなのに読む順が案件の有無で変わる。
 *
 * @param after 案件適用後のコンポーネント（案件を選んでいなければ master のコンポーネント。案件が画面ごと消したなら空）
 * @param before 変更前のコンポーネント。案件を選んでいなければ `null`
 * @returns コンポーネント表の行（コンポーネントの番号の昇順）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面詳細のコンポーネント表の行を、案件適用前後の追加・更新・削除つきで組む。
 * @graph-connects none
 */
export function buildScreenComponentRows(
  after: readonly ScreenComponentRow[],
  before: readonly ScreenComponentRow[] | null,
): readonly ScreenComponentDiffRow[] {
  if (before === null) {
    return after.map((component) => ({
      component,
      previous: null,
      state: 'unchanged' as const,
      changed: NOTHING_CHANGED,
    }));
  }

  const beforeById = new Map(before.map((component) => [component.componentId, component]));
  const afterIds = new Set(after.map((component) => component.componentId));

  const kept = after.map((component): ScreenComponentDiffRow => {
    const previous = beforeById.get(component.componentId);
    if (previous === undefined) {
      return { component, previous: null, state: 'created', changed: NOTHING_CHANGED };
    }
    const changed = changedOf(component, previous);
    return { component, previous, state: anyChanged(changed) ? 'updated' : 'unchanged', changed };
  });

  const removed = before
    .filter((component) => !afterIds.has(component.componentId))
    .map(
      (component): ScreenComponentDiffRow => ({
        component,
        previous: component,
        state: 'deleted',
        changed: NOTHING_CHANGED,
      }),
    );

  return [...kept, ...removed].sort(
    (left, right) => left.component.componentId - right.component.componentId,
  );
}
